<template>
  <div
    class="quantity-selector"
    :class="{
      'quantity-selector--large': large,
      [$class('error')]: state === 'error',
      'has-graduated': showGraduate,
    }"
  >
    <div
      v-if="!deposit"
      class="quantity-selector__control quantity-selector__control--button"
    >
      <button
        class="quantity-selector__button"
        :class="{ [$class('disabled')]: disableDecrease }"
        type="button"
        @click="handleQuantityUpdate('decrease')"
      >
        <span
          class="visually-hidden"
          v-text="$string('cart.line_item.decrease_quantity')"
        />

        <icon-minus />
      </button>
    </div>

    <div class="quantity-selector__control quantity-selector__control--input">
      <label
        class="visually-hidden"
        :for="`quantity-selector-input-${lineItem ? lineItem.key : 'product'}`"
        v-text="$string('cart.line_item.quantity')"
      />

      <input
        :id="`quantity-selector-input-${lineItem ? lineItem.key : 'product'}`"
        class="quantity-selector__input text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
        :name="name"
        pattern="[0-9]*"
        readonly
        tabindex="-1"
        type="number"
        :value="quantity"
      />
    </div>

    <div
      v-if="!deposit"
      class="quantity-selector__control quantity-selector__control--button"
    >
      <button
        class="quantity-selector__button"
        :class="{ [$class('disabled')]: disableIncrease }"
        type="button"
        @click="handleQuantityUpdate('increase')"
      >
        <span
          class="visually-hidden"
          v-text="$string('cart.line_item.increase_quantity')"
        />

        <icon-plus />
      </button>
      <div
        v-if="graduated.length > 0"
        class="quantity-selector__graduated"
        :class="{
          [$class('hidden')]: !showGraduate,
        }"
      >
        <span class="quantity-selector__graduated--icon">
          <icon-discount />
        </span>
        <p
          v-if="showGraduate"
          class="tooltip quantity-selector__graduated--text text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
          :class="{
            success: highestGraduated,
          }"
          v-text="graduatedText"
        />
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Vue: Quantity selector (quantity-selector)
 * -----------------------------------------------------------------------------
 * Base template and functionality for quantity selector.
 * - Used in product form and line item.
 * - Responsible for emitting events to update quantity, parent components must
 *   action the update.
 *
 * @param {Boolean} [large] - Large styles.
 * @param {Object} [lineItem] - Line item object.
 * @param {Number} [min] - The minimum value the selector can be.
 * @param {Number} quantity - Actual line item quantity selector value as
 * reflected in Shopify's data.
 * @param {String} state - Parent component state.
 *
 * @emits updateQuantity - Emit new quantity value.
 *
 */
import IconMinus from '~icons/directional-navigation/minus.svg';
import IconPlus from '~icons/directional-navigation/plus.svg';
import IconDiscount from '~icons/general/discount.svg';

import { mapActions, mapState } from 'pinia';
import useCartStore from '~stores/cart';
import useDiscountCampaignsStore from '~stores/discount-campaigns';

export default {
  name: 'QuantitySelector',

  components: {
    IconMinus,
    IconPlus,
    IconDiscount,
  },

  props: {
    large: {
      type: Boolean,
      default: false,
    },
    lineItem: {
      type: [Boolean, Object],
      default: false,
    },
    min: {
      type: Number,
      default: 0,
    },
    quantity: {
      type: Number,
      default: 1,
      required: true,
    },
    state: {
      type: String,
      default: 'loading',
      required: true,
    },
    deposit: {
      type: Boolean,
      default: false,
    },
    itemDependenciesUpdating: {
      type: Boolean,
      default: false,
    },
    graduated: {
      type: Array,
      default: () => [],
    },
  },

  emits: {
    'update-quantity': (payload) => typeof payload === 'number',
  },

  data() {
    return {
      localQuantity: 1,
      showGraduate: false,
      currentGraduated: {},
      localGraduated: [...this.graduated],
      graduatedText: '',
      inCartQuantity: 0,
      // checks final message for maximum graduated discount has been shown
      finalMessageShown: false,
    };
  },

  computed: {
    ...mapState(useCartStore, {
      getCartProductLimitReached: 'getCartProductLimitReached',
      cartItems: (store) => store.response.items,
    }),

    ...mapState(useDiscountCampaignsStore, {
      codeState: 'codeState',
    }),

    /**
     * Compute if decrease button should be disabled.
     * @return {Boolean}
     */
    disableDecrease() {
      return (
        this.unavailable ||
        this.state === 'updating' ||
        this.state === 'loading' ||
        this.localQuantity === this.min ||
        this.itemDependenciesUpdating
      );
    },

    /**
     * Compute if increase button should be disabled.
     * @return {Boolean}
     */
    disableIncrease() {
      const outOfInventory =
        this.lineItem.inventory_management === 'shopify' &&
        this.lineItem.inventory_policy === 'deny' &&
        this.quantity >= this.inventory;

      return (
        this.unavailable ||
        this.state === 'updating' ||
        this.state === 'loading' ||
        this.getCartProductLimitReached ||
        outOfInventory ||
        this.itemDependenciesUpdating
      );
    },

    /**
     * Compute inventory.
     * - Returns 0 if no limit.
     * @returns {Number}
     */
    inventory() {
      return this.lineItem?.inventory_quantity || 0;
    },

    /**
     * Compute input name.
     * @return {String}
     */
    name() {
      if (this.lineItem?.key) {
        return 'updates[]';
      }

      return 'quantity';
    },

    /**
     * Compute unavailable.
     * - If object has avaiable = false and no quantity, means it's unavailable
     *   on the product page.
     * @returns {Boolean}
     */
    unavailable() {
      if (!this.lineItem) {
        return false;
      }

      return this.lineItem.available === false && !this.lineItem.quantity;
    },

    /**
     * Compute graduated discount items sorted by quantity.
     * @returns {Array}
     */
    sortedGraduate() {
      return [...this.localGraduated].sort((a, b) => a.quantity - b.quantity);
    },

    /**
     * Compute if graduated discount is visible the highest possible graduate.
     * @returns {Boolean}
     */
    highestGraduated() {
      if (this.graduated.length === 0) {
        return false;
      }

      const highestQuantity = Math.max(
        ...this.localGraduated.map((item) => item.quantity),
      );
      return highestQuantity === this.inCartQuantity;
    },
  },

  watch: {
    /**
     * Watch cart items to update graduated discount.
     * we need to change the graduatedtext whenever cartItems change
     * as different items have an effect on the graduated discount
     */
    cartItems() {
      if (this.graduated.length > 0) {
        this.showGraduate = false;
        this.updateGraduatedText();
      }
    },

    /**
     * Watch actual quantity to update local quantity
     * resets graduated discount
     */
    quantity() {
      this.localQuantity = this.quantity;
      // on change of quantity we reset the graduated discount
      if (this.graduated.length > 0) {
        this.showGraduate = false;
      }
    },
  },

  mounted() {
    this.localQuantity = this.quantity || 1;

    if (this.graduated.length > 0) {
      this.updateGraduatedText();
    }
  },

  methods: {
    ...mapActions(useDiscountCampaignsStore, {
      setCodeState: 'setCodeState',
      setErrorType: 'setErrorType',
    }),
    /**
     * Handle quantity selector update.
     * - Buttons only update local quantity which emits an event.
     * - If the event is successful then the quantity prop is updated and this
     *   is used for front-end display.
     * @param {String} type - `decrease` or `increase`.
     */
    handleQuantityUpdate(type) {
      if (
        this.state === 'updating' ||
        (type === 'decrease' && this.disableDecrease) ||
        (type === 'increase' && this.disableIncrease)
      ) {
        return;
      }

      if (this.codeState === 'invalid') {
        this.setCodeState('ready');
        this.setErrorType('');
      }

      if (type === 'decrease') {
        this.localQuantity--;
      } else {
        this.localQuantity++;
      }

      this.$emit('update-quantity', this.localQuantity);
    },

    /**
     * Fade out tooltip after 3 sec and remove all graduated text.
     */
    fadeOutTooltip() {
      setTimeout(() => {
        this.showGraduate = false;
        this.graduatedText = '';
        this.finalMessageShown = true;
        this.inCartQuantity = 0;
      }, 1500);
    },

    /**
     * Update the graduated text based on the current quantity.
     */
    async updateGraduatedText() {
      // we wait for the next tick to get the updated cartItems
      await this.$nextTick();
      if (this.localGraduated.length === 0) {
        this.graduatedText = '';
        return;
      }

      // we get the matching cart items for graduated discount
      const matchingCartItems = this.getMatchingCartItems(
        this.cartItems,
        this.sortedGraduate,
      );

      // from those matching cart items we get the total quantity
      this.inCartQuantity = matchingCartItems.reduce((total, item) => {
        return total + item.quantity;
      }, 0);

      // get the highest quantity from the graduated discount
      const highestQuantity = Math.max(
        ...this.localGraduated.map((item) => item.quantity),
      );
      const currentGraduated = this.sortedGraduate.find(
        (item) => item.quantity > this.inCartQuantity,
      );

      // we check the matching Cart Items and reset the final message shown
      // as both are affected by the quantity change of one of them
      if (matchingCartItems.length > 1) {
        this.finalMessageShown = false;
      }

      // if the cart quantity is higher than the quantity of graduated discount
      // we don't show anything
      if (this.inCartQuantity > highestQuantity) {
        this.showGraduate = false;
        return;
      }

      if (currentGraduated) {
        this.showGraduate = true;
        this.finalMessageShown = false;
        const leftAmount = currentGraduated.quantity - this.inCartQuantity;
        this.graduatedText = this.$string(
          'discount_campaigns.graduated_discount.cart_missing',
          {
            replace: {
              percentage: currentGraduated.percentage,
              amount: leftAmount,
            },
          },
        );
      } else if (
        highestQuantity === this.inCartQuantity &&
        !this.finalMessageShown
      ) {
        this.showGraduate = true;
        this.fadeOutTooltip();
        this.graduatedText = this.$string(
          'discount_campaigns.graduated_discount.cart_max',
        );
      } else {
        this.showGraduate = false;
        this.graduatedText = '';
      }
    },

    /**
     * Get matching cart items from graduated discount
     * @param {Array} items - cartItems
     * @param {Array} graduates - variants that have graduated discount
     */
    getMatchingCartItems(items, graduates) {
      const matchingCartItems = [];

      // get all variant ids from the graduated discount
      const graduatedVariants = graduates.map((obj) => obj.variantIds).flat();

      // get all variant ids from the cart items
      items.forEach((cartItem) => {
        // Check if the cartItem.variant_id matches any id in variantIdsArray
        if (graduatedVariants.includes(String(cartItem.variant_id))) {
          // Add the matching cart item to the array
          matchingCartItems.push(cartItem);
        }
      });

      // Return the array of matching cart items
      return matchingCartItems;
    },
  },
};
</script>

<style lang="scss">
@import './quantity-selector';
</style>
